import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FLOWS } from '../data/flows';
import '../ds/tokens/fonts.css';
import '../ds/tokens/tokens.css';
import '../ds/styles/components.css';
import '../guia-home.css';
import Button from '../ds/components/Button';
import LoadingDots from '../ds/components/LoadingDots';
import Brujula, { Logo } from '../ds/components/Brujula';
import FondoMosaico from './FondoMosaico';
import CierreHome from './CierreHome';
import { evento, iniciarMedicion } from '../lib/medicion';
import { clasificar, registrarTypeSafe, RUBRO_LABEL, CONFIANZA_MINIMA } from '../lib/typesafe';

const MAX_STEPS = 7;

// Un nodo puede saltearse según una respuesta anterior:
// skipIf: { dataKey, equals, to } → si collected[dataKey] === equals, va a `to`.
const resolverSiguiente = (node, collected) => {
  let n = node;
  while (n?.skipIf && collected[n.skipIf.dataKey] === n.skipIf.equals) n = FLOWS[n.skipIf.to];
  return n;
};

// ── Option chip ────────────────────────────────────────────────────�
const Chip = ({ label, onClick, disabled, index, base = 350 }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="gh-opcion gh-chip-entra"
    style={{ '--d': `${base + index * 60}ms` }}
  >
    {label}
  </button>
);

// ── Multi-select chip ──────────────────────────────────────────────────�
const MultiChip = ({ label, selected, onClick, disabled, index }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
    className="gh-opcion gh-chip-entra"
    style={{ '--d': `${350 + index * 60}ms` }}
  >
    {label}
  </button>
);

// ── Text input ─────────────────────────────────────────────────────
const TextInput = ({ placeholder, etiqueta, onSubmit, disabled, allowEmpty }) => {
  const [val, setVal] = useState('');
  const ref = useRef(null);

  // El foco automático solo con puntero fino: en el teléfono abriría el teclado sin que lo pidan.
  useEffect(() => {
    const fino = window.matchMedia?.('(pointer: fine)').matches;
    if (fino && !disabled && ref.current) ref.current.focus();
  }, [disabled]);

  const submit = (e) => {
    e.preventDefault();
    if (val.trim() || allowEmpty) { onSubmit(val.trim()); setVal(''); }
  };

  return (
    <form
      onSubmit={submit}
      className="gh-campo"
      style={{ animation: 'fadeSlideUp 0.35s ease-out 0.1s both' }}
    >
      <input
        ref={ref}
        type="text"
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder={placeholder || 'Escribí tu respuesta…'}
        aria-label={etiqueta || placeholder || 'Tu respuesta'}
        name="respuesta"
        autoComplete="off"
        disabled={disabled}
        className="gh-input"
      />
      <Button
        variant="solido"
        type="submit"
        disabled={disabled || (!val.trim() && !allowEmpty)}
      >
        Enviar
      </Button>
    </form>
  );
};

// ── Result extras (bullets + CTAs — message stays in the main large font) ─
// Enriquece el mensaje WA con el contexto real del usuario
const resolveHref = (href, collected) => {
  if (!href?.includes('wa.me') || !collected) return href;
  try {
    const url = new URL(href);
    const baseText = decodeURIComponent(url.searchParams.get('text') || '');
    const extras = [];

    // Identidad del negocio
    const name = collected.businessName;
    const info = collected.businessInfo || collected.idea || collected.freeformNeed;
    if (name && info) {
      extras.push(`Soy ${name}: ${info.slice(0, 60)}${info.length > 60 ? '...' : ''}`);
    } else if (info) {
      extras.push(info.slice(0, 80) + (info.length > 80 ? '...' : ''));
    }

    // Cómo los encuentran hoy. El multiselect guarda los labels como un texto
    // ("Redes sociales, Boca en boca"), no como lista de values: esto antes
    // hacía .map() sobre ese string, tiraba excepción, y el catch de abajo se
    // comía TODO el enriquecimiento. El mensaje llegaba pelado.
    const howFound = typeof collected.howFound === 'string' ? collected.howFound.trim() : '';
    if (howFound) {
      extras.push(/nadie/i.test(howFound)
        ? 'Todavía nadie me encuentra'
        : `Hoy me encuentran por ${howFound.toLowerCase()}`);
    }

    // Desde dónde lo mira (ramas de IA y operación)
    const rol = {
      'Soy dueño/a o socio/a': 'Soy dueño/a del negocio',
      'Lidero un equipo o un área': 'Lidero un equipo',
      'Trabajo por mi cuenta': 'Trabajo por mi cuenta',
      'Soy parte de un equipo': 'Soy parte del equipo',
    }[collected.rol];
    if (rol) extras.push(rol);

    // Lo que contó en las ramas de IA, capacitación y operación
    const enPalabrasPropias = collected.iaTarea || collected.operacion;
    if (enPalabrasPropias) {
      extras.push(enPalabrasPropias.slice(0, 90) + (enPalabrasPropias.length > 90 ? '...' : ''));
    }
    const equipo = { 'Solo yo': 'Trabajo solo' }[collected.equipo]
      || (collected.equipo ? `Somos ${collected.equipo.toLowerCase()}` : '');
    if (equipo) extras.push(equipo);
    if (collected.alcanceTaller) extras.push(`Sería para ${collected.alcanceTaller.replace(/^A /, '').toLowerCase()}`);
    if (collected.comoLoLleva) extras.push(`Hoy lo llevo ${collected.comoLoLleva.toLowerCase()}`);

    // Visibilidad en Google
    const searchCtx = {
      'Probablemente no':    'probablemente no aparezco en Google',
      'Nunca lo revisé':     'nunca revisé si aparezco en Google',
      'Sí, tengo Google Ads':'tengo Google Ads activos',
    }[collected.searchability];
    if (searchCtx) extras.push(searchCtx);

    if (!extras.length) return href;
    const enMayuscula = t => t.charAt(0).toUpperCase() + t.slice(1);

    // Inserta el contexto antes de la pregunta de cierre si la hay
    const lastQ = baseText.lastIndexOf('¿');
    const enriched = lastQ > 0
      ? `${baseText.slice(0, lastQ).trim()} ${extras.map(enMayuscula).join('. ')}. ${baseText.slice(lastQ)}`
      : `${baseText} ${extras.map(enMayuscula).join('. ')}.`;

    url.searchParams.set('text', enriched);
    return url.toString();
  } catch {
    return href;
  }
};

// Deja el lead en la planilla cuando la persona se va a WhatsApp. Es fire and
// forget a propósito: si la planilla falla, WhatsApp se abre igual.
const guardarLead = (node, collected) => {
  try {
    // rubroSugerido va a la pestaña TypeSafe (desde WebChat), no a la fila del lead.
    const { businessName, rubroSugerido, ...resto } = collected || {};
    fetch('/.netlify/functions/save-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nombre: businessName || '',
        contacto: '',
        servicio: node?.servicio || node?.id || '',
        respuestas: resto,
        origen: 'chat',
      }),
    }).catch(() => {});
  } catch {}
};

const ResultExtras = ({ node, onRestart, onNavigate, collected }) => {
  if (node.cierre) {
    return <CierreHome node={node} collected={collected} escribimeHref={resolveHref(node.ctas.find(c => c.href)?.href, collected)} onNavigate={onNavigate} onRestart={onRestart} />;
  }
  const n = node.bullets?.length || 0;
  return (
    <div className="gh-resultado">
      <div className="gh-puntos">
        {(node.bullets || []).map((b, i) => (
          <div
            key={i}
            className="gh-punto"
            style={{ animation: `fadeSlideUp 0.4s ease-out ${0.1 + i * 0.07}s both` }}
          >
            {b}
          </div>
        ))}
      </div>

      {node.priceNote && (
        <p className="gh-nota" style={{ animation: `fadeSlideUp 0.4s ease-out ${0.12 + n * 0.07}s both` }}>
          {node.priceNote}
        </p>
      )}

      <div className="gh-acciones" style={{ animation: `fadeSlideUp 0.4s ease-out ${0.18 + n * 0.07}s both` }}>
        {node.ctas.map((cta, i) => (
          cta.action === 'navigate' ? (
            <Button key={i} variant="solido" onClick={onNavigate}>{cta.label}</Button>
          ) : (
            <a
              key={i}
              href={resolveHref(cta.href, collected)}
              onClick={() => guardarLead(node, collected)}
              target="_blank"
              rel="noopener noreferrer"
              className="guia-btn"
            >
              <svg className="guia-btn__arc guia-btn__arc--l" viewBox="0 0 16 52" aria-hidden="true"><path d="M 14 0 C -2 14 -2 38 14 52" /></svg>
              <span>{cta.label}</span>
              <svg className="guia-btn__arc guia-btn__arc--r" viewBox="0 0 16 52" aria-hidden="true"><path d="M 2 0 C 18 14 18 38 2 52" /></svg>
            </a>
          )
        ))}
      </div>

      <button
        onClick={onRestart}
        className="gh-sutil"
        style={{ animation: `fadeSlideUp 0.4s ease-out ${0.28 + n * 0.07}s both` }}
      >
        ← nueva consulta
      </button>
    </div>
  );
};

// ── El primer mensaje de GUIA: primero piensa, después habla ─────────
const PrimerMensaje = ({ texto }) => {
  const [habla, setHabla] = useState(false);
  useEffect(() => {
    const quieto = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const id = setTimeout(() => setHabla(true), quieto ? 0 : 1100);
    return () => clearTimeout(id);
  }, []);
  return (
    <div className="gh-burbuja" aria-live="polite">
      {habla
        ? <span className="gh-burbuja__texto">{texto}</span>
        : <LoadingDots size={8} />}
    </div>
  );
};

// ── Progress dots ────────────────────────────────────────────────────
const ProgressDots = ({ step, total }) => (
  <div className="gh-progreso" role="progressbar" aria-label="Avance de la consulta" aria-valuemin={0} aria-valuemax={total} aria-valuenow={step} aria-valuetext={`Paso ${step} de ${total}`}>
    {Array.from({ length: total }).map((_, i) => (
      <i key={i} data-hecho={i < step} />
    ))}
  </div>
);

// ── Main ──────────────────────────────────────────────────────────────────
const ConversationalExperience = () => {
  const navigate = useNavigate();
  const [phase, setPhase]             = useState('landing');
  const [currentNode, setCurrentNode] = useState(null);
  const [displayText, setDisplayText] = useState('');
  const [fullText, setFullText]       = useState('');   // el mensaje entero: reserva el lugar de lo que todavía no se dijo
  const mensajeRef = useRef(null);
  const [frozen, setFrozen]           = useState(false);
  const [step, setStep]               = useState(0);
  const [visible, setVisible]         = useState(true);
  const [collected, setCollected]     = useState({});
  const [multiSelected, setMultiSelected] = useState([]);
  const typingRef = useRef(null);
  // Sube con cada reinicio: una clasificación que vuelve tarde no se mezcla
  // con la conversación siguiente.
  const conversacionRef = useRef(0);

  // Word-by-word typewriter — feels like thinking, not printing
  const typewrite = (text, onDone) => {
    const parts = text.split(' ');
    let i = 0;
    setFullText(text);
    setDisplayText('');
    clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      i++;
      setDisplayText(parts.slice(0, i).join(' '));
      if (i >= parts.length) {
        clearInterval(typingRef.current);
        onDone && onDone();
      }
    }, 60);
  };

  const transition = (fn) => {
    setVisible(false);
    setTimeout(() => { fn(); setVisible(true); }, 520);
  };

  // Inject name into result messages if we have it
  const buildMessage = (node, collectedOverride) => {
    const c = collectedOverride ?? collected;
    let msg = node.message;
    if (node.isResult && c.name) {
      msg = `Hola, ${c.name}.\n\n${msg}`;
    }
    return msg;
  };

  // La landing ya es el primer turno de la charla: elegir una opción de acá
  // es contestar «¿Qué te trae?» sin pasar por un botón de arranque.
  const handleEleccionInicial = (opt) => {
    setStep(1);
    handleOption(opt);
  };

  const handleOption = (opt) => {
    if (frozen) return;
    setFrozen(true);
    const newCollected = currentNode?.dataKey ? { ...collected, [currentNode.dataKey]: opt.label } : collected;
    const next = resolverSiguiente(FLOWS[opt.next], newCollected);
    if (!next) return;
    setStep(s => Math.min(s + 1, MAX_STEPS));

    if (currentNode?.dataKey) {
      setCollected(prev => ({ ...prev, [currentNode.dataKey]: opt.label }));
    }

    transition(() => { setPhase('typing'); setDisplayText(''); setCurrentNode(next); });
    setTimeout(() => {
      typewrite(buildMessage(next), () => {
        setPhase(next.isResult ? 'result' : 'question');
        setFrozen(false);
      });
    }, 350);
  };

  const handleText = (text) => {
    if (frozen) return;
    if (!text.trim() && !currentNode?.skippable) return;
    if (!currentNode?.next) return;
    setFrozen(true);
    let next = FLOWS[currentNode.next];
    if (!next) return;
    setStep(s => Math.min(s + 1, MAX_STEPS));

    let newCollected = collected;
    if (currentNode?.dataKey) {
      newCollected = { ...collected, [currentNode.dataKey]: text.trim() };
      setCollected(newCollected);
    }
    next = resolverSiguiente(next, newCollected);

    // Corre en paralelo mientras la persona contesta lo que sigue.
    const conversacion = conversacionRef.current;
    const guardar = dato => {
      if (conversacionRef.current === conversacion) setCollected(prev => ({ ...prev, ...dato }));
    };
    if (currentNode?.dataKey === 'businessInfo' && text.trim()) {
      clasificar('rubro', text).then(r => {
        if (!r) return;
        const rubro = r.confidence >= CONFIANZA_MINIMA && RUBRO_LABEL[r.choice];
        // rubroSugerido viaja siempre (aunque no pre-llene) para registrar
        // después qué dejó la persona en WebChat.
        guardar({
          rubroSugerido: {
            valor: RUBRO_LABEL[r.choice] || 'Otro',
            confianza: r.confidence,
            // La ganadora muestra la misma confianza que decide completar o
            // preguntar; si no, su barra podría cruzar la línea del 70% en un
            // caso donde igual se preguntó.
            opciones: (r.top || []).map(o => ({
              valor: RUBRO_LABEL[o.choice] || 'Otro',
              p: o.choice === r.choice ? r.confidence : o.p,
            })),
          },
          ...(rubro ? { rubro } : {}),
        });
      });
    }
    // Modo sombra: el resultado NO cambia lo que ve la persona (sigue
    // freeform_result). Solo viaja a la planilla con el lead, para comparar
    // contra lo que se resolvió por WhatsApp antes de rutear en vivo.
    if (currentNode?.dataKey === 'freeformNeed' && text.trim()) {
      clasificar('servicio', text).then(r => {
        if (!r) return;
        guardar({ servicioSugerido: `${r.choice} (${Math.round(r.confidence * 100)}%)` });
        // El final se completa a mano en la planilla con lo que se resolvió por WhatsApp.
        registrarTypeSafe({ pregunta: 'servicio', texto: text.trim(), sugerencia: r.choice, confianza: r.confidence });
      });
    }

    transition(() => { setPhase('typing'); setDisplayText(''); setCurrentNode(next); });
    setTimeout(() => {
      typewrite(buildMessage(next, newCollected), () => {
        setPhase(next.isResult ? 'result' : 'question');
        setFrozen(false);
      });
    }, 350);
  };

  const handleMultiToggle = (opt) => {
    setMultiSelected(prev => {
      if (opt.exclusive) {
        // Exclusive: solo este, o deseleccionar si ya estaba
        return prev.includes(opt.value) ? [] : [opt.value];
      }
      // Quitar cualquier exclusivo que haya
      const withoutExclusive = prev.filter(v => {
        const o = currentNode?.options?.find(x => x.value === v);
        return !o?.exclusive;
      });
      if (withoutExclusive.includes(opt.value)) {
        return withoutExclusive.filter(v => v !== opt.value);
      }
      return [...withoutExclusive, opt.value];
    });
  };

  const handleMultiConfirm = () => {
    if (frozen || !multiSelected.length) return;
    if (!currentNode?.next) return;
    setFrozen(true);
    let next = FLOWS[currentNode.next];
    if (!next) return;
    setStep(s => Math.min(s + 1, MAX_STEPS));

    // Guardamos los labels de las opciones seleccionadas como string legible
    const selectedLabels = currentNode.options
      .filter(o => multiSelected.includes(o.value))
      .map(o => o.label)
      .join(', ');

    let newCollected = collected;
    if (currentNode?.dataKey) {
      newCollected = { ...collected, [currentNode.dataKey]: selectedLabels };
      setCollected(newCollected);
    }
    next = resolverSiguiente(next, newCollected);
    setMultiSelected([]);

    transition(() => { setPhase('typing'); setDisplayText(''); setCurrentNode(next); });
    setTimeout(() => {
      typewrite(buildMessage(next, newCollected), () => {
        setPhase(next.isResult ? 'result' : 'question');
        setFrozen(false);
      });
    }, 350);
  };

  const handleNavigate = () => {
    if (!currentNode?.navTo) return;
    navigate(currentNode.navTo, {
      state: { type: currentNode.navType, dest: currentNode.navDest, passState: collected },
    });
  };

  const handleRestart = () => {
    clearInterval(typingRef.current);
    conversacionRef.current += 1;
    setPhase('landing');
    setCurrentNode(null);
    setDisplayText('');
    setFrozen(false);
    setStep(0);
    setVisible(true);
    setCollected({});
    setMultiSelected([]);
  };

  useEffect(() => () => clearInterval(typingRef.current), []);
  useEffect(() => iniciarMedicion(), []);
  // Cada pantalla de la charla se mide por su id; nunca por lo que la persona escribió.
  useEffect(() => {
    if (phase === 'question' || phase === 'result') evento('paso_visto', { pantalla: currentNode?.id });
  }, [phase, currentNode?.id]);

  // Cada pantalla nueva lleva el foco a su mensaje (para teclado y lector de pantalla)
  useEffect(() => {
    if (phase === 'question' || phase === 'result') mensajeRef.current?.focus({ preventScroll: true });
  }, [phase, currentNode?.id]);

  const showEscapeHatch = !currentNode?.noEscape && currentNode?.id !== 'freeform' && phase === 'question';

  return (
    <div className="gh-root" data-theme="claro">
      <FondoMosaico
        pantalla={currentNode?.id || 'inicio'}
        avance={0.35 + 0.65 * (phase === 'result' ? 1 : step / MAX_STEPS)}
      />

      {/* Header */}
      <header className="gh-header">
        <button
          onClick={step > 0 ? handleRestart : undefined}
          className="gh-logo"
          data-activo={step > 0}
          tabIndex={step > 0 ? 0 : -1}
          aria-label={step > 0 ? 'GUIA: empezar de nuevo' : 'GUIA'}
        >
          <Brujula sobreClaro />
        </button>

        <div className="gh-header__lado">
          {/* La puerta a quién está atrás de esto. */}
          <span className="gh-pildora"><a href="/antonela" className="gh-link"><span className="gh-link__largo">¿Quién está detrás de todo esto?</span><span className="gh-link__corto">¿Quién está detrás?</span></a></span>
          {step > 0 && <span className="gh-pildora"><ProgressDots step={phase === 'result' ? MAX_STEPS : step} total={MAX_STEPS} /></span>}
        </div>
      </header>

      {/* Stage */}
      <main className="gh-escena">
        <div className="gh-panel" data-fase={phase === 'landing' ? 'landing' : 'charla'} >

        {/* Landing: la frase es la composición; la charla ya empezó, y sube como el agua */}
        {phase === 'landing' && (
          <div
            className="gh-landing"
            style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.3s ease' }}
          >
            <div className="gh-superficie">
              <h1 className="gh-titular gh-entra" style={{ '--d': '0ms' }} aria-label="¿La IA es para vos?">
                <span aria-hidden="true">
                  ¿La{' '}
                  <span className="gh-ia">I<span className="gh-a"><Logo variante="simbolo" /></span></span>
                  {' '}es para vos?
                </span>
              </h1>
            </div>

            <div className="gh-mar">
            <svg className="gh-ola" viewBox="0 0 1440 56" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0 30C180 6 360 6 540 28S900 54 1080 30 1320 8 1440 24" />
            </svg>

            <div className="gh-agua">
              <PrimerMensaje texto={FLOWS['start'].message} />
              <div className="gh-opciones gh-opciones--charla">
                {FLOWS['start'].options.map((opt, i) => (
                  <Chip
                    key={opt.label}
                    label={opt.label}
                    onClick={() => handleEleccionInicial(opt)}
                    disabled={frozen}
                    index={i}
                    base={1200}
                  />
                ))}
              </div>
              <p className="gh-firma">Transformamos problemas humanos en soluciones digitales.</p>
            </div>
            </div>
          </div>
        )}

        {/* Typing / Question / Result — unified block, same font size */}
        {(phase === 'typing' || phase === 'question' || phase === 'result') && (
          <div
            className="gh-bloque"
            style={{
              opacity: visible ? 1 : 0,
              filter: visible ? 'blur(0px)' : 'blur(3px)',
              transform: visible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease, filter 0.3s ease',
            }}
          >
            {/* Message — same size throughout typing AND result */}
            <div className="gh-texto" aria-hidden="true">
              {(fullText || displayText).split('\n\n').map((parte, i, todas) => {
                const antes = todas.slice(0, i).reduce((n, q) => n + q.length + 2, 0);
                const dicho = displayText.slice(antes, antes + parte.length);
                return (
                  <p key={i} className={i === 0 ? 'gh-mensaje' : 'gh-cuerpo'}>
                    {dicho}
                    {phase === 'typing' && displayText && dicho.length < parte.length && <span className="gh-cursor" />}
                    <span className="gh-resto">{parte.slice(dicho.length)}</span>
                  </p>
                );
              })}
            </div>
            {/* el mensaje entero, una sola vez y cuando termina de escribirse */}
            <div className="gh-sr" role="status" aria-live="polite" tabIndex={-1} ref={mensajeRef}>
              {phase === 'typing' ? null : displayText.split('\n\n').map((parte, i) => (i === 0 ? <h1 key={i}>{parte}</h1> : <p key={i}>{parte}</p>))}
            </div>

            {/* El silencio de antes de hablar, hecho visible */}
            {phase === 'typing' && !displayText && (
              <LoadingDots className="gh-pensando" size={9} />
            )}

            {/* Question options */}
            {phase === 'question' && (
              <div className="flex flex-col items-center gap-4 w-full">
                {currentNode?.input ? (
                  <>
                    <TextInput
                      placeholder={currentNode.placeholder}
                      etiqueta={currentNode.message}
                      onSubmit={handleText}
                      disabled={frozen}
                      allowEmpty={currentNode?.skippable}
                    />
                    {currentNode?.skippable && (
                      <button
                        onClick={() => handleText('')}
                        className="gh-sutil"
                        style={{ animation: 'fadeSlideUp 0.35s ease-out 0.2s both' }}
                      >
                        Continuar sin decirlo →
                      </button>
                    )}
                    {showEscapeHatch && (
                      <button
                        onClick={() => handleOption({ label: 'Tengo otra necesidad', next: 'freeform' })}
                        className="gh-sutil"
                      >
                        Tengo otra necesidad →
                      </button>
                    )}
                  </>
                ) : currentNode?.multiselect ? (
                  <>
                    <div className="gh-opciones">
                      {currentNode?.options?.map((opt, i) => (
                        <MultiChip
                          key={opt.value}
                          label={opt.label}
                          selected={multiSelected.includes(opt.value)}
                          onClick={() => handleMultiToggle(opt)}
                          disabled={frozen}
                          index={i}
                        />
                      ))}
                    </div>
                    <div style={{ animation: 'fadeSlideUp 0.4s ease-out 0.4s both' }}>
                      <Button
                        variant="solido"
                        onClick={handleMultiConfirm}
                        disabled={frozen || !multiSelected.length}
                      >
                        Continuar
                      </Button>
                    </div>
                    {showEscapeHatch && (
                      <button
                        onClick={() => { setMultiSelected([]); handleOption({ label: 'Tengo otra necesidad', next: 'freeform' }); }}
                        className="gh-sutil"
                        style={{ animation: 'fadeSlideUp 0.4s ease-out 0.5s both' }}
                      >
                        Tengo otra necesidad →
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="gh-opciones">
                      {currentNode?.options?.map((opt, i) => (
                        <Chip
                          key={opt.label}
                          label={opt.label}
                          onClick={() => handleOption(opt)}
                          disabled={frozen}
                          index={i}
                        />
                      ))}
                    </div>
                    {showEscapeHatch && (
                      <button
                        onClick={() => handleOption({ label: 'Tengo otra necesidad', next: 'freeform' })}
                        className="gh-sutil"
                        style={{ animation: 'fadeSlideUp 0.4s ease-out 0.4s both' }}
                      >
                        Tengo otra necesidad →
                      </button>
                    )}
                  </>
                )}

                <button onClick={handleRestart} className="gh-sutil">
                  ← empezar de nuevo
                </button>
              </div>
            )}

            {/* Result extras — message stays above at large size */}
            {phase === 'result' && currentNode && (
              <ResultExtras
                node={currentNode}
                onRestart={handleRestart}
                onNavigate={handleNavigate}
                collected={collected}
              />
            )}
          </div>
        )}
        </div>
      </main>

      {/* Footer */}
      <footer className="gh-pie">
        <span>Córdoba, Argentina</span>
        <Link to="/privacidad">Privacidad</Link>
      </footer>
    </div>
  );
};

export default ConversationalExperience;
